import { useState, useEffect } from 'react'
import { Navigate, useNavigate } from 'react-router-dom'
import { useAuth } from '../../contexts/AuthContext'
import { entitlementsOf } from '../../utils/entitlements'
import { trainingBlockApi } from '../../api'
import { trainingProgramApi } from '../../api/trainingProgramApi'
import type { ParsedTrainingProgram } from '../../api/trainingProgramApi'
import type { ParsedTrainingBlock, TrainingTagsGroupedDto } from '../../api/trainingBlockApi'
import { BLOCK_TAG_CATEGORIES, PROGRAM_TAG_CATEGORIES } from '../../api/trainingBlockApi'
import { BlockItemsEditor } from './BlockItemsEditor'
import { BlockIndexLabel } from './BlockIndexLabel'
import { itemsFromParsed, itemsToApi, type ItemRow } from './blockItemHelpers'
import { TagSelector } from './TagSelector'
import type { ApiError } from '../../api/client'
import { Button, Badge } from '../ui'
import { useTranslation } from '../../i18n/useTranslation'
import { useTheme } from '../../hooks/useTheme'
import { getFormErrorStyle, getInputStyle, getSectionCardStyle, INPUT_CLASS_NAME, TEXTAREA_CLASS_NAME } from '../../utils/themeStyles'
import {
  AI_PARSE_MIN_CHARS,
  AI_PARSE_PROGRAM_MAX_CHARS,
  AI_PARSE_REPARSE_COMMENT_MAX_CHARS,
  mapParseApiErrorMessage,
  parseTextValidationMessage,
  reparseCommentValidationMessage,
  validateParseRawText,
  validateReparseComment,
} from '../../utils/aiParseValidation'

const MAX_CORRECTIONS = 3

interface DayState {
  name: string
  selectedTags: string[]
  items: ItemRow[]
  collapsed: boolean
}

export default function CreateTrainingProgramFromText() {
  const navigate = useNavigate()
  const theme = useTheme()
  const { t, translate } = useTranslation()
  const { user } = useAuth()
  const blocked = !entitlementsOf(user).createOwnBlocksAndPrograms

  const [rawText, setRawText] = useState('')
  const [parsed, setParsed] = useState<ParsedTrainingProgram | null>(null)
  const [programName, setProgramName] = useState('')
  const [programTags, setProgramTags] = useState<string[]>([])
  const [days, setDays] = useState<DayState[]>([])
  const [availableTags, setAvailableTags] = useState<TrainingTagsGroupedDto>({
    muscles: [], levels: [], genders: [], types: [],
  })
  const [correction, setCorrection] = useState('')
  const [correctionsUsed, setCorrectionsUsed] = useState(0)
  const [sessionId, setSessionId] = useState<string | null>(null)
  const [parsing, setParsing] = useState(false)
  const [reparsing, setReparsing] = useState(false)
  const [confirming, setConfirming] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const itemLabels = {
    itemsLabel: t.createTrainingBlock.exercisesLabel,
    addExercise: t.createTrainingBlock.addExercise,
    addToGroup: t.createTrainingBlock.addToGroup,
    notePlaceholder: t.createTrainingBlock.notePlaceholder,
    addNote: t.createTrainingBlock.addNote,
    changeNote: t.createTrainingBlock.changeNote,
    doneNote: t.createTrainingBlock.doneNote,
    exerciseNamePlaceholder: t.createTrainingBlock.exerciseNamePlaceholder,
    groupLabel: t.createTrainingBlock.groupLabel,
  }

  useEffect(() => {
    trainingBlockApi.getTags().then(setAvailableTags).catch(() => {})
  }, [])

  if (blocked) {
    return <Navigate to="/training-programs" replace />
  }

  function applyParsed(result: ParsedTrainingProgram) {
    setParsed(result)
    setProgramName(result.name)
    setDays(
      (result.days ?? []).map(day => ({
        name: day.name,
        selectedTags: day.tags ?? [],
        items: itemsFromParsed(day.items),
        collapsed: false,
      }))
    )
  }

  function dayToParsedBlock(day: DayState): ParsedTrainingBlock {
    return {
      name: day.name.trim(),
      tags: day.selectedTags,
      items: itemsToApi(day.items).map(item => ({
        note: item.note ?? null,
        exercises: item.exercises,
      })),
    }
  }

  const rawTextValidation = validateParseRawText(rawText, AI_PARSE_PROGRAM_MAX_CHARS)
  const correctionValidation = validateReparseComment(correction)
  const canParse = !rawTextValidation && !parsing
  const canReparse = !correctionValidation && !reparsing && !!correction.trim()

  async function handleParse() {
    const validationError = validateParseRawText(rawText, AI_PARSE_PROGRAM_MAX_CHARS)
    if (validationError) {
      setError(parseTextValidationMessage(validationError, translate, AI_PARSE_PROGRAM_MAX_CHARS))
      return
    }
    setError(null)
    setParsing(true)
    try {
      const response = await trainingProgramApi.parseProgram(rawText.trim())
      setSessionId(response.sessionId)
      applyParsed(response.result)
      setCorrectionsUsed(0)
      setCorrection('')
    } catch (err) {
      const apiError = err as ApiError
      if (apiError.status === 429) {
        setError(apiError.message || t.createTrainingProgramFromText.error.quotaExceeded)
      } else {
        setError(mapParseApiErrorMessage(
          apiError.message,
          translate,
          AI_PARSE_PROGRAM_MAX_CHARS,
          t.createTrainingProgramFromText.error.parseFailed,
        ))
      }
    } finally {
      setParsing(false)
    }
  }

  async function handleReparse() {
    if (!parsed || !sessionId) return
    const validationError = validateReparseComment(correction)
    if (validationError) {
      setError(reparseCommentValidationMessage(validationError, translate))
      return
    }
    setError(null)
    setReparsing(true)
    try {
      const currentProgram: ParsedTrainingProgram = {
        name: programName.trim() || parsed.name,
        days: days.map(dayToParsedBlock),
      }
      const response = await trainingProgramApi.reparseProgram(sessionId, currentProgram, correction.trim())
      setSessionId(response.sessionId)
      applyParsed(response.result)
      setCorrectionsUsed(prev => prev + 1)
      setCorrection('')
    } catch (err) {
      const apiError = err as ApiError
      if (apiError.status === 429) {
        setError(apiError.message || t.createTrainingProgramFromText.error.quotaExceeded)
      } else {
        setError(mapParseApiErrorMessage(
          apiError.message,
          translate,
          AI_PARSE_PROGRAM_MAX_CHARS,
          t.createTrainingProgramFromText.error.parseFailed,
        ))
      }
    } finally {
      setReparsing(false)
    }
  }

  function updateDay(index: number, patch: Partial<DayState>) {
    setDays(prev => prev.map((d, i) => (i === index ? { ...d, ...patch } : d)))
  }

  async function handleConfirm() {
    if (!parsed) return
    setError(null)
    setConfirming(true)
    try {
      const blockIds: number[] = []
      for (const day of days) {
        const block = await trainingBlockApi.createBlock({
          name: day.name.trim(),
          tags: day.selectedTags,
          items: itemsToApi(day.items),
        })
        blockIds.push(block.id)
      }
      await trainingProgramApi.createProgram({
        name: programName.trim() || parsed.name,
        tags: programTags,
        blockIds,
      })
      navigate('/training-programs')
    } catch (err) {
      const apiError = err as ApiError
      setError(apiError.message || t.createTrainingProgramFromText.error.createFailed)
    } finally {
      setConfirming(false)
    }
  }

  function handleStartOver() {
    setParsed(null)
    setProgramName('')
    setProgramTags([])
    setDays([])
    setCorrection('')
    setCorrectionsUsed(0)
    setSessionId(null)
    setError(null)
  }

  const correctionsLeft = MAX_CORRECTIONS - correctionsUsed
  const canCorrect = correctionsLeft > 0

  return (
    <div className="p-5 pb-24">
      <header className="mb-6">
        <div className="flex items-center gap-2 flex-wrap mb-1">
          <h1 className="text-2xl font-bold" style={{ color: theme.text.primary }}>
            {t.createTrainingProgramFromText.title}
          </h1>
          <Badge variant="warning" size="sm">Experimental</Badge>
        </div>
        <p className="text-sm" style={{ color: theme.text.muted }}>
          {t.createTrainingProgramFromText.subtitle}
        </p>
      </header>

      {!parsed ? (
        <div className="flex flex-col gap-5">
          <div>
            <label className="block text-sm font-medium mb-2" style={{ color: theme.text.primary }}>
              {t.createTrainingProgramFromText.textareaLabel}
            </label>
            <textarea
              value={rawText}
              onChange={e => {
                setRawText(e.target.value)
                setError(null)
              }}
              placeholder={t.createTrainingProgramFromText.textareaPlaceholder}
              rows={10}
              maxLength={AI_PARSE_PROGRAM_MAX_CHARS}
              className={`${TEXTAREA_CLASS_NAME} text-sm`}
              style={getInputStyle(theme)}
            />
            <p className="text-xs mt-1" style={{ color: theme.text.muted }}>
              {translate('aiParse.validation.hint', {
                min: AI_PARSE_MIN_CHARS,
                max: AI_PARSE_PROGRAM_MAX_CHARS,
              })}
            </p>
            {rawText.length > 0 && rawTextValidation && (
              <p className="text-xs mt-1" style={{ color: '#ef4444' }}>
                {parseTextValidationMessage(rawTextValidation, translate, AI_PARSE_PROGRAM_MAX_CHARS)}
              </p>
            )}
          </div>

          {error && (
            <div className="rounded-xl p-4" style={getFormErrorStyle()}>
              <p className="text-sm" style={{ color: theme.text.primary }}>{error}</p>
            </div>
          )}

          <Button
            type="button"
            variant="primary"
            size="lg"
            fullWidth
            disabled={!canParse}
            onClick={handleParse}
          >
            {parsing ? t.createTrainingProgramFromText.parsing : t.createTrainingProgramFromText.parseButton}
          </Button>
        </div>
      ) : (
        <div className="flex flex-col gap-5">
          <div>
            <label className="block text-sm font-medium mb-2" style={{ color: theme.text.primary }}>
              {t.createTrainingProgramFromText.programNameLabel}
            </label>
            <input
              type="text"
              value={programName}
              onChange={e => setProgramName(e.target.value)}
              placeholder={t.createTrainingProgramFromText.programNamePlaceholder}
              className={`${INPUT_CLASS_NAME} text-sm`}
              style={getInputStyle(theme)}
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-2" style={{ color: theme.text.primary }}>
              {t.createTrainingBlock.tagsLabel}
            </label>
            <TagSelector
              catalog={availableTags}
              categories={PROGRAM_TAG_CATEGORIES}
              selected={programTags}
              onChange={setProgramTags}
            />
          </div>

          {days.map((day, dayIndex) => (
            <div key={dayIndex} className="rounded-2xl overflow-hidden" style={getSectionCardStyle(theme)}>
              <div
                className="flex items-center gap-3 p-4 cursor-pointer"
                onClick={() => updateDay(dayIndex, { collapsed: !day.collapsed })}
              >
                <div className="flex-1 min-w-0" onClick={e => e.stopPropagation()}>
                  <BlockIndexLabel index={dayIndex + 1} className="block mb-1" />
                  <input
                    type="text"
                    value={day.name}
                    onChange={e => updateDay(dayIndex, { name: e.target.value })}
                    placeholder={t.createTrainingProgramFromText.blockNameLabel}
                    className="w-full bg-transparent text-sm font-medium focus:outline-none"
                    style={{ color: theme.text.primary }}
                  />
                </div>
                <span className="shrink-0 text-xs" style={{ color: theme.text.muted }}>{day.collapsed ? '▼' : '▲'}</span>
              </div>

              {!day.collapsed && (
                <div className="flex flex-col gap-4 px-4 pb-4" style={{ borderTop: `1px solid ${theme.cardBorder}` }}>
                  <div className="pt-4">
                    <label className="block text-xs font-medium mb-2" style={{ color: theme.text.muted }}>
                      {t.createTrainingBlock.tagsLabel}
                    </label>
                    <TagSelector
                      catalog={availableTags}
                      categories={BLOCK_TAG_CATEGORIES}
                      selected={day.selectedTags}
                      onChange={tags => updateDay(dayIndex, { selectedTags: tags })}
                    />
                  </div>

                  <BlockItemsEditor
                    items={day.items}
                    onChange={items => updateDay(dayIndex, { items })}
                    textColor={theme.text.primary}
                    bgColor={theme.bgColor}
                    labels={itemLabels}
                  />
                </div>
              )}
            </div>
          ))}

          {canCorrect && (
            <div>
              <label className="block text-sm font-medium mb-2" style={{ color: theme.text.primary }}>
                {t.createTrainingProgramFromText.correctionsLabel}
                <span className="ml-2 font-normal text-xs" style={{ color: theme.text.muted }}>
                  {t.createTrainingProgramFromText.attemptsLeft.replace('{{count}}', String(correctionsLeft))}
                </span>
              </label>
              <textarea
                value={correction}
                onChange={e => {
                  setCorrection(e.target.value)
                  setError(null)
                }}
                placeholder={t.createTrainingProgramFromText.correctionPlaceholder}
                rows={3}
                maxLength={AI_PARSE_REPARSE_COMMENT_MAX_CHARS}
                className={`${TEXTAREA_CLASS_NAME} text-sm mb-2`}
                style={getInputStyle(theme)}
              />
              {correction.length > 0 && correctionValidation && (
                <p className="text-xs mb-2" style={{ color: '#ef4444' }}>
                  {reparseCommentValidationMessage(correctionValidation, translate)}
                </p>
              )}
              <Button
                type="button"
                variant="secondary"
                size="md"
                fullWidth
                disabled={!canReparse}
                onClick={handleReparse}
              >
                {reparsing ? t.createTrainingProgramFromText.reparsing : t.createTrainingProgramFromText.reparseButton}
              </Button>
            </div>
          )}

          {error && (
            <div className="rounded-xl p-4" style={getFormErrorStyle()}>
              <p className="text-sm" style={{ color: theme.text.primary }}>{error}</p>
            </div>
          )}

          <Button
            type="button"
            variant="primary"
            size="lg"
            fullWidth
            disabled={confirming || !programName.trim()}
            onClick={handleConfirm}
          >
            {confirming ? t.createTrainingProgramFromText.confirming : t.createTrainingProgramFromText.confirmButton}
          </Button>

          <Button
            type="button"
            variant="secondary"
            size="md"
            fullWidth
            onClick={handleStartOver}
          >
            {t.createTrainingProgramFromText.startOver}
          </Button>
        </div>
      )}
    </div>
  )
}
